// ABOUTME: Splits the filters a table opens with between its column filters and its hidden extra filters.
// ABOUTME: Shown columns get theirs through the session filter model the data-table restores; the others stay hidden.

import type { ColumnState } from '@ds/data-table';
import type { SearchFilter } from '@src/__generated__/types';

type GridContentType = { code?: string | null; hidden?: boolean | null };
type GridSchema = {
    contentTypeGroups?: Array<{
        code?: string | null;
        contentTypes?: GridContentType[] | null;
    } | null> | null;
};

/**
 * Ids (`<group>-<contentType>`) of the content type columns the table shows: the schema's
 * non-hidden content types, minus those the column layout or visibility turns off.
 */
export function getShownContentColumnIds(
    schema: GridSchema,
    columnState?: ColumnState[] | null,
    colsVisibility?: Record<string, boolean> | null,
): Set<string> {
    const hidden = new Set([
        ...(columnState ?? []).filter(state => !state.isActive).map(state => state.columnName),
        ...Object.entries(colsVisibility ?? {})
            .filter(([, visible]) => !visible)
            .map(([id]) => id),
    ]);
    return new Set(
        (schema.contentTypeGroups ?? []).flatMap(group =>
            (group?.contentTypes ?? [])
                .filter(ct => !ct.hidden)
                .map(ct => `${group?.code}-${ct.code}`)
                .filter(id => !hidden.has(id)),
        ),
    );
}

// sessionStorage key prefix of the filter model a data-table with an `id` restores on mount.
const DATA_TABLE_FILTERS_KEY_PREFIX = 'dt-filters-';

/** Stores `filters` as the filter model the data-table `tableId` restores when it mounts. */
export function seedTableFilters(tableId: string, filters: SearchFilter[]): void {
    try {
        globalThis.sessionStorage?.setItem(
            `${DATA_TABLE_FILTERS_KEY_PREFIX}${tableId}`,
            JSON.stringify({
                items: filters.map(({ field, operator, value }) => ({ field, operator, value })),
            }),
        );
    } catch {
        // Without a session storage the table opens unfiltered by its columns.
    }
}

/**
 * The filters on a shown column seed that column's filter, where the user sees and changes them;
 * the others are applied as hidden extra filters.
 */
export function splitPresetFilters(
    filters: SearchFilter[],
    shownColumnIds: Set<string>,
): { columnFilters: SearchFilter[]; hiddenFilters: SearchFilter[] } {
    return {
        columnFilters: filters.filter(filter => shownColumnIds.has(filter.field)),
        hiddenFilters: filters.filter(filter => !shownColumnIds.has(filter.field)),
    };
}
